import React from "react";

const newId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const keyOf = (item, i) => item._id || `${i}`;
import { Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const F = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{label} {hint && <span className="text-slate-600 normal-case tracking-normal">({hint})</span>}</Label>
    {children}
  </div>
);
const cls = "bg-slate-950/60 border-slate-700 text-sm";

const Stars = ({ value, onChange }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" onClick={() => onChange(n)} className={`text-lg leading-none ${n <= value ? "text-amber-400" : "text-slate-700"}`} aria-label={`${n} stars`}>★</button>
    ))}
  </div>
);

function ReviewsSettings({ reviews, set }) {
  const r = reviews || { rating: 0, count: 0, google_url: "", items: [] };
  const items = r.items || [];
  const setR = (k, v) => set(["site", "reviews", k], v);
  const setItem = (i, k, v) => set(["site", "reviews", "items", i, k], v);
  const add = () => setR("items", [...items, { _id: newId(), name: "", stars: 5, text: "", date: "" }]);
  const del = (i) => setR("items", items.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3" data-testid="site-reviews-settings">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Google reviews <span className="text-slate-600 normal-case tracking-normal">(curated — shown on the home page)</span></p>
        <Button type="button" variant="ghost" size="sm" onClick={add} data-testid="site-review-add" className="text-amber-300 hover:text-amber-200 hover:bg-amber-500/10 h-7 text-xs"><Plus className="w-3.5 h-3.5 mr-1" /> Add review</Button>
      </div>
      <div className="grid sm:grid-cols-[120px_120px_1fr] gap-3">
        <F label="Rating" hint="e.g. 4.9"><Input type="number" step="0.1" min="0" max="5" value={r.rating ?? ""} onChange={(e) => setR("rating", e.target.value === "" ? 0 : Number(e.target.value))} data-testid="site-review-rating" className={`${cls} h-9 font-mono-num`} /></F>
        <F label="Review count"><Input type="number" min="0" value={r.count ?? ""} onChange={(e) => setR("count", e.target.value === "" ? 0 : Number(e.target.value))} data-testid="site-review-count" className={`${cls} h-9 font-mono-num`} /></F>
        <F label="Google listing URL" hint="Maps / 'Write a review' link"><Input value={r.google_url || ""} placeholder="https://g.page/r/…" onChange={(e) => setR("google_url", e.target.value)} data-testid="site-review-url" className={`${cls} h-9`} /></F>
      </div>
      {items.map((it, i) => (
        <div key={keyOf(it, i)} className="rounded-lg border border-slate-800 bg-slate-950/30 p-3 grid sm:grid-cols-[180px_120px_1fr_32px] gap-2 items-start" data-testid={`site-review-row-${i}`}>
          <Input value={it.name} placeholder="Reviewer name" onChange={(e) => setItem(i, "name", e.target.value)} data-testid={`site-review-name-${i}`} className={`${cls} h-9`} />
          <div className="space-y-1"><Stars value={it.stars || 5} onChange={(v) => setItem(i, "stars", v)} /><Input value={it.date || ""} placeholder="e.g. Mar 2026" onChange={(e) => setItem(i, "date", e.target.value)} data-testid={`site-review-date-${i}`} className={`${cls} h-8 text-xs`} /></div>
          <Textarea rows={2} value={it.text} placeholder="What the customer said…" onChange={(e) => setItem(i, "text", e.target.value)} data-testid={`site-review-text-${i}`} className={cls} />
          <button type="button" onClick={() => del(i)} data-testid={`site-review-del-${i}`} className="h-9 w-8 grid place-items-center text-slate-500 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
    </div>
  );
}

// Prices → Website tab: public site copy + general pricing chart
export default function WebsiteSettings({ site, set, sym }) {
  const s = site || {};
  const rows = s.public_pricing || [];
  const setRow = (i, k, v) => set(["site", "public_pricing", i, k], v);
  const addRow = () => set(["site", "public_pricing"], [...rows, { _id: newId(), label: "", small: 0, medium: 0, large: 0 }]);
  const delRow = (i) => set(["site", "public_pricing"], rows.filter((_, idx) => idx !== i));
  const services = s.services || [];
  const setSvc = (i, k, v) => set(["site", "services", i, k], v);

  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-4">
        <F label="Hero headline"><Input value={s.hero_title || ""} onChange={(e) => set(["site", "hero_title"], e.target.value)} data-testid="site-set-hero-title" className={cls} /></F>
        <F label="Opening hours"><Input value={s.opening_hours || ""} onChange={(e) => set(["site", "opening_hours"], e.target.value)} data-testid="site-set-hours" className={cls} /></F>
        <div className="sm:col-span-2"><F label="Hero subtitle"><Textarea rows={2} value={s.hero_subtitle || ""} onChange={(e) => set(["site", "hero_subtitle"], e.target.value)} data-testid="site-set-hero-subtitle" className={cls} /></F></div>
        <div className="sm:col-span-2"><F label="About us"><Textarea rows={5} value={s.about || ""} onChange={(e) => set(["site", "about"], e.target.value)} data-testid="site-set-about" className={cls} /></F></div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">General pricing chart <span className="text-slate-600 normal-case tracking-normal">(shown publicly as "from {sym}X"; 0 = "Ask us")</span></p>
          <Button type="button" variant="ghost" size="sm" onClick={addRow} data-testid="site-pricing-add-row" className="text-amber-300 hover:text-amber-200 hover:bg-amber-500/10 h-7 text-xs"><Plus className="w-3.5 h-3.5 mr-1" /> Add row</Button>
        </div>
        <div className="hidden sm:grid grid-cols-[1fr_repeat(3,100px)_32px] gap-2 px-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold"><span>Service</span><span>Small</span><span>Medium</span><span>Large</span><span /></div>
        {rows.map((r, i) => (
          <div key={keyOf(r, i)} className="grid grid-cols-1 sm:grid-cols-[1fr_repeat(3,100px)_32px] gap-2 items-center rounded-lg border border-slate-800 bg-slate-950/30 p-2" data-testid={`site-pricing-row-${i}`}>
            <Input value={r.label} placeholder="e.g. Full Car Respray (same colour)" onChange={(e) => setRow(i, "label", e.target.value)} data-testid={`site-pricing-label-${i}`} className={`${cls} h-9`} />
            <div className="grid grid-cols-3 sm:contents gap-2">
              {["small", "medium", "large"].map((k) => (
                <div key={k} className="relative"><span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs">{sym}</span>
                  <Input type="number" value={r[k] ?? ""} onChange={(e) => setRow(i, k, e.target.value === "" ? 0 : Number(e.target.value))} data-testid={`site-pricing-${k}-${i}`} className={`${cls} h-9 pl-6 font-mono-num`} /></div>
              ))}
            </div>
            <button type="button" onClick={() => delRow(i)} data-testid={`site-pricing-del-${i}`} className="h-9 w-8 grid place-items-center text-slate-500 hover:text-rose-400 justify-self-end"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <F label="Pricing note"><Input value={s.pricing_note || ""} onChange={(e) => set(["site", "pricing_note"], e.target.value)} data-testid="site-set-pricing-note" className={cls} /></F>
      </div>

      <ReviewsSettings reviews={s.reviews} set={set} />

      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Services shown on the website</p>
        {services.map((sv, i) => (
          <div key={keyOf(sv, i)} className="grid sm:grid-cols-[200px_1fr] gap-2">
            <Input value={sv.title} onChange={(e) => setSvc(i, "title", e.target.value)} data-testid={`site-service-title-${i}`} className={`${cls} h-9`} />
            <Input value={sv.desc} onChange={(e) => setSvc(i, "desc", e.target.value)} data-testid={`site-service-desc-${i}`} className={`${cls} h-9`} />
          </div>
        ))}
      </div>
    </div>
  );
}
